import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  styled,
  TextField,
  Tooltip,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { AtomIcon, FileTextIcon, LockKeyholeIcon, LockKeyholeOpenIcon, PanelLeftCloseIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCaseApi } from '../../services/api/useCase.api';
import { useCountyApi } from '../../services/api/useCounty.api';
import { useGenderApi } from '../../services/api/useGender.api';
import { useRaceApi } from '../../services/api/useRace.api';
import { useUserApi } from '../../services/api/useUser.api';
import { Case } from '../../types/Case/Case.type';
import { CaseCreate } from '../../types/Case/CaseCreate.type';
import { CaseUpdate } from '../../types/Case/CaseUpdate.type';
import { Statute } from '../../types/Statute.type';
import { splitEnhancementGroup } from '../../utils/enhancementDyads';
import StatuteAutocomplete from '../StatuteAutocomplete/StatuteAutocomplete';
import EnhancementsButton from './EnhancementsButton';
import EnhancementsDialog, { ChargeEnhancement } from './EnhancementsDialog';

const StyledFormControl = styled(FormControl)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const StyledLabel = styled('label')({
  fontSize: '14px',
});

const RequiredLabel = styled('label')({
  fontSize: '14px',
  '&::after': {
    content: '" *"',
    color: 'red',
  },
});

interface SearchPanelProps {
  onSubmit: (searchData: CaseCreate) => void;
  isCreatingCase: boolean;
  caseData?: Case;
  isLoadingCase?: boolean;
  onHidePanel?: () => void;
  onDisparityUnselect: () => void;
  onSsbSearch?: (newCase: CaseCreate | null) => void;
}

function SearchPanel({
  onSubmit,
  isCreatingCase,
  caseData,
  isLoadingCase,
  onHidePanel,
  onDisparityUnselect,
  onSsbSearch,
}: SearchPanelProps) {
  const queryClient = useQueryClient();
  const { getUserInfo } = useUserApi();
  const { getAssignedCounties } = useCountyApi();
  const { getRaces } = useRaceApi();
  const { getGenders } = useGenderApi();
  const { updateCase } = useCaseApi();

  function buildChargeEnhancementsFromCase(caseData: Case): ChargeEnhancement[] {
    const groups = caseData.charge_enhancements;
    if (!groups?.length) return [];

    return groups
      .map((group) => {
        const { enhancements, bases } = splitEnhancementGroup(group);
        return {
          id: crypto.randomUUID(),
          enhancements,
          assignedChargeIds: bases.map((s) => s.id),
        };
      })
      .filter((e) => e.enhancements.length > 0);
  }

  const [county, setCounty] = useState<number | null>(() => caseData?.county?.id ?? null);
  const [birthday, setBirthday] = useState<Date | null>(() =>
    caseData?.client_birthday ? parseISO(caseData.client_birthday) : null,
  );
  const [race, setRace] = useState<number | null>(() => caseData?.client_race?.id ?? null);
  const [gender, setGender] = useState<number | null>(() => caseData?.client_gender?.id ?? null);
  const [caseCharges, setCaseCharges] = useState<Statute[]>(
    () => caseData?.statutes?.filter((s) => !s.is_enhancement) ?? [],
  );
  const [enhancementStatutes, setEnhancementStatutes] = useState<Statute[]>(
    () => caseData?.statutes?.filter((s) => s.is_enhancement) ?? [],
  );
  const [offenseDate, setOffenseDate] = useState<Date | null>(() =>
    caseData?.offense_date ? parseISO(caseData.offense_date) : null,
  );
  const [chargeEnhancements, setChargeEnhancements] = useState<ChargeEnhancement[]>(() =>
    caseData ? buildChargeEnhancementsFromCase(caseData) : [],
  );
  const [isEnhancementsDialogOpen, setIsEnhancementsDialogOpen] = useState(false);
  const [isNameDialogOpen, setIsNameDialogOpen] = useState(false);
  const [nameDialogTarget, setNameDialogTarget] = useState<'case' | 'ssb'>('case');
  const [caseName, setCaseName] = useState('');

  const { data: counties } = useQuery({
    queryKey: ['assignedCounties'],
    queryFn: getAssignedCounties,
  });

  const { data: userInfo } = useQuery({
    queryKey: ['userInfo'],
    queryFn: getUserInfo,
  });

  const { data: races } = useQuery({
    queryKey: ['races'],
    queryFn: () => getRaces({ comparison: false }),
  });

  const { data: genders } = useQuery({
    queryKey: ['genders'],
    queryFn: () => getGenders(),
  });

  const { mutate: lockUnlockCase } = useMutation({
    mutationKey: ['lockUnlockCase', caseData?.id],
    mutationFn: ({ caseId, is_locked }: { caseId: number; is_locked: boolean }) => updateCase(caseId, { is_locked }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case'], exact: false });
    },
  });

  const { mutate: patchCase, isPending: isUpdatingCase } = useMutation({
    mutationKey: ['patchCase', caseData?.id],
    mutationFn: (payload: Partial<CaseUpdate>) => updateCase(Number(caseData?.id), payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['disparityDetails'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['caseExportData'], exact: false });
      onDisparityUnselect();
    },
  });

  const [prevCaseData, setPrevCaseData] = useState(caseData);
  const [prevUserInfo, setPrevUserInfo] = useState<typeof userInfo>(undefined);
  if (caseData !== prevCaseData || userInfo !== prevUserInfo) {
    setPrevCaseData(caseData);
    setPrevUserInfo(userInfo);

    if (!caseData) {
      setCounty(userInfo?.team?.county?.id || null);
      setBirthday(null);
      setRace(null);
      setGender(null);
      setCaseCharges([]);
      setEnhancementStatutes([]);
      setChargeEnhancements([]);
      setOffenseDate(null);
    } else {
      setCounty(caseData.county.id || null);
      setBirthday(caseData.client_birthday ? parseISO(caseData.client_birthday) : null);
      setRace(caseData.client_race.id || null);
      setGender(caseData.client_gender?.id || null);
      setOffenseDate(caseData.offense_date ? parseISO(caseData.offense_date) : null);
    }
  }

  // Initialize case statutes when caseData changes
  const [initializedCaseId, setInitializedCaseId] = useState<number | null>(() => caseData?.id ?? null);
  if (caseData && initializedCaseId !== caseData.id && county === (caseData.county?.id ?? null)) {
    if (caseData.statutes?.length) {
      const regularStatutes = caseData.statutes.filter((s) => !s.is_enhancement);
      const enhancements = caseData.statutes.filter((s) => s.is_enhancement);
      setCaseCharges(regularStatutes);
      setEnhancementStatutes(enhancements);
    } else {
      setCaseCharges([]);
      setEnhancementStatutes([]);
    }
    setChargeEnhancements(buildChargeEnhancementsFromCase(caseData));
    setInitializedCaseId(caseData.id);
  }

  const handleCountyChange = (event: SelectChangeEvent<number>) => {
    const newCountyId = event.target.value as number;
    setCounty(newCountyId);
    setCaseCharges([]);
    setEnhancementStatutes([]);
    setChargeEnhancements([]);
  };

  const handleCaseChargesChange = (newCharges: Statute[]) => {
    setCaseCharges(newCharges);
    const validIds = new Set(newCharges.map((s) => s.id));
    setChargeEnhancements((prev) =>
      prev
        .map((e) => ({ ...e, assignedChargeIds: e.assignedChargeIds.filter((id) => validIds.has(id)) }))
        .filter((e) => e.assignedChargeIds.length > 0 || e.enhancements.length === 0),
    );
  };

  const handleEnhancementsApply = (newCaseEnhancements: Statute[], newChargeEnhancements: ChargeEnhancement[]) => {
    setEnhancementStatutes(newCaseEnhancements);
    setChargeEnhancements(newChargeEnhancements);
  };

  const handleBirthdayChange = (newValue: Date | null) => {
    setBirthday(newValue);
  };

  const handleRaceChange = (event: SelectChangeEvent<number>) => {
    setRace(event.target.value as number);
  };

  const handleGenderChange = (event: SelectChangeEvent<number>) => {
    setGender(event.target.value as number);
  };

  const handleOffenseDateChange = (newValue: Date | null) => {
    setOffenseDate(newValue);
  };

  const isFormValid = county !== null && caseCharges.length > 0 && race !== null;

  const caseModified = useMemo(() => {
    if (!caseData) return false;

    // Helper functions to compare arrays and dates
    const sortedIds = (statutes: Statute[]) => statutes.map((s) => s.id).sort((a, b) => a - b);
    const statuteIdsEqual = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);
    const hasDateChanged = (date: Date | null, iso?: string | null) => {
      if (date === null) return !iso;
      if (!iso) return false;
      return date.getTime() === parseISO(iso).getTime();
    };

    if (caseData.county?.id !== county) return true;
    if (caseData.client_race?.id !== race) return true;
    if ((caseData.client_gender?.id ?? null) !== (gender ?? null)) return true;
    if (!hasDateChanged(birthday, caseData.client_birthday)) return true;
    if (!hasDateChanged(offenseDate, caseData.offense_date)) return true;
    if (!statuteIdsEqual(sortedIds(caseCharges), sortedIds(caseData.statutes?.filter((s) => !s.is_enhancement) ?? [])))
      return true;
    if (
      !statuteIdsEqual(
        sortedIds(enhancementStatutes),
        sortedIds(caseData.statutes?.filter((s) => s.is_enhancement) ?? []),
      )
    )
      return true;

    // Compare charge enhancements
    const normalizeGroup = (enh: number[], assigned: number[]) =>
      JSON.stringify({ e: [...enh].sort((a, b) => a - b), a: [...assigned].sort((a, b) => a - b) });

    const currentGroups = chargeEnhancements
      .filter((e) => e.enhancements.length > 0)
      .map((e) =>
        normalizeGroup(
          e.enhancements.map((s) => s.id),
          e.assignedChargeIds,
        ),
      )
      .sort();

    const savedGroups = (caseData.charge_enhancements ?? [])
      .map((group) => {
        const { enhancements, bases } = splitEnhancementGroup(group);
        return normalizeGroup(
          enhancements.map((s) => s.id),
          bases.map((s) => s.id),
        );
      })
      .sort();

    if (currentGroups.length !== savedGroups.length) return true;
    return currentGroups.some((g, i) => g !== savedGroups[i]);
  }, [caseData, county, race, gender, birthday, offenseDate, caseCharges, enhancementStatutes, chargeEnhancements]);

  const buildCasePayload = () => ({
    formattedBirthday: birthday ? format(birthday, 'yyyy-MM-dd') : null,
    formattedOffenseDate: offenseDate ? format(offenseDate, 'yyyy-MM-dd') : null,
    allStatuteIds: [...caseCharges.map((s) => s.id), ...enhancementStatutes.map((s) => s.id)],
    chargeEnhancementsPayload: chargeEnhancements
      .filter((e) => e.enhancements.length > 0)
      .map((e) => [...e.enhancements.map((s) => s.id), ...e.assignedChargeIds]),
  });

  const handleSubmitClick = () => {
    setNameDialogTarget('case');
    setIsNameDialogOpen(true);
  };

  const handleSsbSearchClick = () => {
    // create new case if not existing
    if (!caseData) {
      setNameDialogTarget('ssb');
      setIsNameDialogOpen(true);
      return;
    }

    onSsbSearch?.(null);
  };

  const handleUpdateClick = () => {
    if (!caseData) return;

    const { formattedBirthday, formattedOffenseDate, allStatuteIds, chargeEnhancementsPayload } = buildCasePayload();

    patchCase({
      county_id: county as number,
      client_birthday: formattedBirthday ?? undefined,
      client_race_id: race !== null ? race : undefined,
      client_gender_id: gender !== null ? gender : undefined,
      statutes_ids: allStatuteIds,
      offense_date: formattedOffenseDate ?? undefined,
      charge_enhancements: chargeEnhancementsPayload,
    });
  };

  const handleDialogClose = () => {
    setIsNameDialogOpen(false);
    setCaseName('');
  };

  const handleCaseNameSubmit = () => {
    if (!caseName.trim()) return;

    const { formattedBirthday, formattedOffenseDate, allStatuteIds, chargeEnhancementsPayload } = buildCasePayload();

    const searchData: CaseCreate = {
      name: caseName.trim(),
      county_id: county as number,
      client_birthday: formattedBirthday,
      client_race_id: race as number,
      client_gender_id: gender ? (gender as number) : null,
      statutes_ids: allStatuteIds,
      offense_date: formattedOffenseDate,
      charge_enhancements: chargeEnhancementsPayload.length > 0 ? chargeEnhancementsPayload : null,
    };

    if (nameDialogTarget === 'ssb') {
      onSsbSearch?.({ ...searchData, is_from_builder: true });
    } else {
      onSubmit(searchData);

      // Reset form
      setCounty(userInfo?.team?.county?.id || null);
      setBirthday(null);
      setRace(null);
      setGender(null);
      setCaseCharges([]);
      setEnhancementStatutes([]);
      setChargeEnhancements([]);
      setOffenseDate(null);
    }

    // Close dialog and reset case name
    setIsNameDialogOpen(false);
    setCaseName('');
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: '0 0 236px',
          width: '236px',
          marginRight: '10px',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        id='tour-search-panel'
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {!caseData && <FileTextIcon />}
            {caseData &&
              (caseData.is_locked ? (
                <Tooltip title='Unlock Case'>
                  <IconButton
                    sx={{ color: 'black' }}
                    onClick={() => lockUnlockCase({ caseId: caseData.id, is_locked: false })}
                  >
                    <LockKeyholeIcon size={22} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title='Lock Case'>
                  <IconButton
                    sx={{ color: 'black' }}
                    onClick={() => lockUnlockCase({ caseId: caseData.id, is_locked: true })}
                  >
                    <LockKeyholeOpenIcon size={22} />
                  </IconButton>
                </Tooltip>
              ))}
            <span style={{ fontSize: '18px', fontWeight: 600 }}>Case Details</span>
          </Box>
          {caseData && onHidePanel && (
            <Tooltip title='Hide case details' placement='right'>
              <IconButton aria-label='Hide case details' onClick={onHidePanel}>
                <PanelLeftCloseIcon size={22} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {isLoadingCase ? (
          <>
            <StyledFormControl>
              <Skeleton variant='text' sx={{ fontSize: '14px' }} />
              <Skeleton variant='rounded' height={60} />
            </StyledFormControl>
            <StyledFormControl>
              <Skeleton variant='text' sx={{ fontSize: '14px' }} />
              <Skeleton variant='rounded' height={60} />
            </StyledFormControl>
            <StyledFormControl>
              <Skeleton variant='text' sx={{ fontSize: '14px' }} />
              <Skeleton variant='rounded' height={60} />
            </StyledFormControl>
            <StyledFormControl>
              <Skeleton variant='text' sx={{ fontSize: '14px' }} />
              <Skeleton variant='rounded' height={60} />
            </StyledFormControl>
            <StyledFormControl>
              <Skeleton variant='text' sx={{ fontSize: '14px' }} />
              <Skeleton variant='rounded' height={60} />
            </StyledFormControl>
            <StyledFormControl>
              <Skeleton variant='text' sx={{ fontSize: '14px' }} />
              <Skeleton variant='rounded' height={60} />
            </StyledFormControl>
            <StyledFormControl>
              <Skeleton variant='rounded' height={40} />
            </StyledFormControl>
          </>
        ) : (
          <>
            <StyledFormControl>
              <RequiredLabel>County</RequiredLabel>
              <Select
                disabled={caseData?.is_locked}
                renderValue={(selected: string | number | readonly string[]) => {
                  return (
                    counties?.find((c) => c.id === selected)?.name || (
                      <span style={{ color: 'rgba(0, 0, 0, 0.42)' }}>Currently N/A</span>
                    )
                  );
                }}
                displayEmpty
                value={county || ''}
                onChange={handleCountyChange}
              >
                {counties && counties.length > 0 ? (
                  counties.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value=''>
                    No assigned datasets
                  </MenuItem>
                )}
              </Select>
            </StyledFormControl>

            <StyledFormControl>
              <RequiredLabel>Case charges</RequiredLabel>
              <StatuteAutocomplete
                countyId={county}
                value={caseCharges}
                onChange={handleCaseChargesChange}
                disabled={caseData?.is_locked}
                isEnhancement={false}
              />
            </StyledFormControl>

            <StyledFormControl>
              <RequiredLabel>Client race</RequiredLabel>
              <Select
                disabled={caseData?.is_locked}
                renderValue={(selected: string | number | readonly string[]) => {
                  return (
                    races?.find((r) => r.id === selected)?.name || (
                      <span style={{ color: 'rgba(0, 0, 0, 0.42)' }}>Currently N/A</span>
                    )
                  );
                }}
                displayEmpty
                value={race || ''}
                onChange={handleRaceChange}
              >
                {races?.map((raceOption) => (
                  <MenuItem key={raceOption.id} value={raceOption.id}>
                    {raceOption.name}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>

            <StyledFormControl>
              <StyledLabel>Client birthday</StyledLabel>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={caseData?.is_locked}
                  disableFuture
                  value={birthday}
                  onChange={handleBirthdayChange}
                  slotProps={{
                    textField: {
                      slotProps: {
                        input: {
                          sx: {
                            fontSize: '14px',
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </StyledFormControl>

            <StyledFormControl>
              <StyledLabel>Client gender</StyledLabel>
              <Select
                disabled={caseData?.is_locked}
                renderValue={(selected: string | number | readonly string[]) => {
                  return (
                    genders?.find((g) => g.id === selected)?.name || (
                      <span style={{ color: 'rgba(0, 0, 0, 0.42)' }}>Currently N/A</span>
                    )
                  );
                }}
                displayEmpty
                value={gender || ''}
                onChange={handleGenderChange}
              >
                {genders?.map((genderOption) => (
                  <MenuItem key={genderOption.id} value={genderOption.id}>
                    {genderOption.name}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>

            <StyledFormControl>
              <StyledLabel>Offense date</StyledLabel>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={caseData?.is_locked}
                  disableFuture
                  value={offenseDate}
                  onChange={handleOffenseDateChange}
                  slotProps={{
                    textField: {
                      slotProps: {
                        input: {
                          sx: {
                            fontSize: '14px',
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </StyledFormControl>

            <EnhancementsButton
              caseCharges={caseCharges}
              enhancementStatutes={enhancementStatutes}
              chargeEnhancements={chargeEnhancements}
              disabled={!!caseData?.is_locked}
              onOpen={() => setIsEnhancementsDialogOpen(true)}
            />

            <Button
              variant='contained'
              color='primary'
              fullWidth
              disableElevation
              sx={{ fontWeight: 600, fontSize: '16px' }}
              data-testid='case-submit-button'
              onClick={caseData ? handleUpdateClick : handleSubmitClick}
              disabled={
                caseData
                  ? caseData.is_locked || !isFormValid || !caseModified || isUpdatingCase
                  : isCreatingCase || !isFormValid
              }
            >
              {caseData ? 'Update' : 'Discovery Snapshot'}
            </Button>

            {onSsbSearch && (
              <Button
                variant='contained'
                color='primary'
                fullWidth
                disableElevation
                startIcon={<AtomIcon size={21} />}
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  background: 'linear-gradient(90deg, #4A6BB5 0%, #2B4A72 100%)',
                  transition: 'all 0.3s ease-in-out',
                  '&.Mui-disabled': {
                    background: '#D0D5D9',
                    color: '#9A9EA1',
                  },
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
                onClick={handleSsbSearchClick}
                disabled={!isFormValid}
              >
                Prima Facie Analysis
              </Button>
            )}
          </>
        )}
      </Box>
      <Dialog open={isNameDialogOpen} onClose={handleDialogClose} maxWidth='sm' fullWidth>
        <DialogTitle>Name Your Case</DialogTitle>
        <DialogContent>
          <TextField
            margin='dense'
            label='Case Name'
            fullWidth
            variant='standard'
            value={caseName}
            onChange={(e) => setCaseName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && caseName.trim()) {
                handleCaseNameSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleCaseNameSubmit} variant='contained' disabled={!caseName.trim()} disableElevation>
            Create Case
          </Button>
        </DialogActions>
      </Dialog>
      <EnhancementsDialog
        open={isEnhancementsDialogOpen}
        onClose={() => setIsEnhancementsDialogOpen(false)}
        onApply={handleEnhancementsApply}
        initialCaseEnhancements={enhancementStatutes}
        initialChargeEnhancements={chargeEnhancements}
        caseCharges={caseCharges}
        countyId={county}
        disabled={!!caseData?.is_locked}
      />
    </>
  );
}

export default SearchPanel;
